import { HttpContext } from "@angular/common/http";
import { inject } from "@angular/core";
import { ActivatedRouteSnapshot, ResolveFn } from "@angular/router";
import { catchError, combineLatest, map, Observable, of } from "rxjs";
import { pathParamsHelper } from "../helpers/path-params.helper";
import { DefaultArgument } from "../pia-api/generated";
import { PiaProductDetailService } from "../pia-api/pia-product-detail.service";
import { IS_IN_RESOLVER_CONTEXT } from "../tokens/resolver-context.token";
import { withLoading } from "../utils/with-loading";

export const compareArgumentsResolver: ResolveFn<
  Observable<DefaultArgument[][]>
> = withLoading((route: ActivatedRouteSnapshot) => {
  const piaProductDetailService = inject(PiaProductDetailService);
  const { productDetailID, competitorID, countryID, languageID, year } =
    pathParamsHelper(route);
  const context = new HttpContext().set(IS_IN_RESOLVER_CONTEXT, true);

  const comparisonArguments: Observable<DefaultArgument[][]> = combineLatest([
    piaProductDetailService.getProductIncludesByContextAndProductId(
      {
        countryID,
        languageID,
        year,
        productID: productDetailID,
      },
      context,
    ),
    piaProductDetailService.getProductIncludesByContextAndProductId(
      {
        countryID,
        languageID,
        year,
        productID: competitorID,
      },
      context,
    ),
    piaProductDetailService.getProductExcludesByContextAndProductId(
      {
        countryID,
        languageID,
        year,
        productID: productDetailID,
      },
      context,
    ),
    piaProductDetailService.getProductExcludesByContextAndProductId(
      {
        countryID,
        languageID,
        year,
        productID: competitorID,
      },
      context,
    ),
    piaProductDetailService.getArgumentsByContext(
      {
        countryID,
        languageID,
        year,
      },
      context,
    ),
  ]).pipe(
    map(
      ([
        productIncludeResponse,
        competitorIncludeResponse,
        productExcludeResponse,
        competitorExcludeResponse,
        argumentsResponse,
      ]) => {
        const productIncludes = productIncludeResponse?.d?.results ?? [];
        const productExcludes = productExcludeResponse?.d?.results ?? [];
        const competitorIncludes = competitorIncludeResponse?.d?.results ?? [];
        const competitorExcludes = competitorExcludeResponse?.d?.results ?? [];
        const contextArguments = argumentsResponse?.d?.results ?? [];

        const productExcludeArgumentIds = new Set(
          productExcludes.map((exclude) => exclude.ArgumentId),
        );
        const competitorExcludeArgumentIds = new Set(
          competitorExcludes.map((exclude) => exclude.ArgumentId),
        );
        const productIncludeArgumentIds = productIncludes
          .map((include) => include.ArgumentId)
          .filter((includeId) => !competitorExcludeArgumentIds.has(includeId));
        const competitorIncludeArgumentIds = competitorIncludes
          .map((include) => include.ArgumentId)
          .filter((includeId) => !productExcludeArgumentIds.has(includeId));

        const productArguments = contextArguments?.filter((contextArgument) =>
          productIncludeArgumentIds?.includes(contextArgument.ID),
        );

        const competitorArguments = contextArguments?.filter(
          (contextArgument) =>
            competitorIncludeArgumentIds?.includes(contextArgument.ID),
        );

        return [productArguments, competitorArguments];
      },
    ),
    catchError(() => of([])),
  );

  return comparisonArguments;
});
